import "./dutyDrawer.css";
import { Drawer, Image, Skeleton, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getCustomerPortalDutyProofQuery } from "@src/util/query/portal.query";
import { useDutyReports } from "@src/util/portal/useDutyReports";
import { googleMapsUrl } from "@src/util/portal/download";
import { formatHours, officerName, sourceLabel, stateMeta, timeRange } from "@src/util/portal/dutyState";

// One duty from Today or the Roster: who, when, its status, the proof of
// attendance (clock in/out with selfie + GPS, every check call / checkpoint)
// and the same duty reports the Schedules & Activity page offers.

const EVENT_STATE = {
    onTime:  { label: "On time", color: "#16a34a" },
    late:    { label: "Late",    color: "#e8590c" },
    missed:  { label: "Missed",  color: "#dc2626" },
    pending: { label: "Due",     color: "#7c98b6" },
};

const Pill = ({ label, color, bg }) => (
    <span className="cdb-pill" style={{ color, borderColor: color, background: bg || "#fff" }}>{label}</span>
);

// where it happened: the same map image as the duty report PDF (made by the
// server), opening Google Maps on that spot
// geofence: was it inside the geofence the app checked it against (circle /
// boundary drawn on the map, pin green inside, red outside)
const MiniMap = ({ location, map, geofence }) => (
    <div className="dd-mapbox">
        <a href={googleMapsUrl(location)} target="_blank" rel="noreferrer" title="Open in Google Maps">
            {map ? <img src={map} alt="Map of where this was done" /> : <span className="dd-map-empty">Map unavailable</span>}
        </a>
        {geofence && <div className={`dd-geo dd-geo-${geofence.status}`}>{geofence.text}</div>}
        <a className="dd-map" href={googleMapsUrl(location)} target="_blank" rel="noreferrer">Open in Google Maps ›</a>
    </div>
);

const Fact = ({ label, children }) => (
    <div className="dd-fact">
        <span className="dd-fact-label">{label}</span>
        <span className="dd-fact-value">{children}</span>
    </div>
);

const clockText = (c, emptyText) => {
    if (!c) return emptyText;
    const parts = [dayjs(c.at).format("HH:mm")];
    if (c.booked) parts.push("booked");
    if (c.late) parts.push("late");
    if (c.automated) parts.push("automatic");
    return parts.join(" · ");
};

export const DutyDrawer = ({ duty, onClose }) => {
    const navigate = useNavigate();
    const { isBusy, downloadReport, downloadDetailDutyReport, downloadDutyDetailPdf } = useDutyReports();

    const { data, loading } = useQuery(getCustomerPortalDutyProofQuery, {
        variables: { source: duty?.source, id: duty?._id },
        skip: !duty?._id,
        fetchPolicy: "network-only",
    });
    const proof = data?.getCustomerPortalDutyProof?.response;
    const meta = stateMeta(duty?.state);
    const isMobile = duty?.source === "mobileService";
    const isCompleted = duty?.state === "completed";

    const busyIcon = <Spin indicator={<LoadingOutlined style={{ fontSize: 14, color: "#0091ae" }} spin />} />;

    return (
        <Drawer
            open={!!duty}
            onClose={onClose}
            width={Math.min(520, window.innerWidth)}
            title={null}
            closable={false}
            className="dd-drawer"
            destroyOnClose
        >
            {duty && (
                <div className="dd">
                    <div className="dd-head">
                        <div className="dd-head-main">
                            <div className="cdb-eyebrow">{sourceLabel(duty)} · {dayjs(duty.start).format("ddd D MMM YYYY")}</div>
                            <h2 className="dd-title">{duty.siteName}</h2>
                            <div className="dd-sub">{timeRange(duty.start, duty.end)} · {duty.dutyType || sourceLabel(duty)}</div>
                        </div>
                        <button type="button" className="dd-close" onClick={onClose} aria-label="Close">×</button>
                    </div>

                    <div className="dd-status">
                        <Pill label={meta.label} color={meta.color} bg={meta.bg} />
                        <span className="dd-officer">{officerName(duty)}</span>
                    </div>

                    <div className="dd-facts">
                        <Fact label={isMobile ? "Started" : "Clock in"}>{clockText(duty.clockIn, "—")}</Fact>
                        <Fact label={isMobile ? "Finished" : "Clock out"}>{clockText(duty.clockOut, "—")}</Fact>
                        <Fact label="Booked">{formatHours(duty.hours)}</Fact>
                        {/* a mobile job is a visit, not a shift */}
                        <Fact label="On duty">{isMobile ? "Not applicable" : formatHours(duty.actualHours)}</Fact>
                        {!isMobile && <Fact label="Check calls">{duty.checkCalls?.due ? `${Math.min(duty.checkCalls.done, duty.checkCalls.due)} of ${duty.checkCalls.due}` : "None due"}</Fact>}
                        {!isMobile && <Fact label="Checkpoints">{duty.checkpoints?.due ? `${Math.min(duty.checkpoints.done, duty.checkpoints.due)} of ${duty.checkpoints.due}` : "None due"}</Fact>}
                    </div>

                    <section className="dd-section">
                        <h3>Proof of attendance</h3>
                        {isMobile && proof?.route && (
                            <div className="dd-route">
                                <a href={googleMapsUrl(proof.route.finish)} target="_blank" rel="noreferrer" title="Open the finish point in Google Maps">
                                    <img src={proof.route.map} alt="The route recorded on the app" />
                                </a>
                                <div className="dd-route-note"><span className="dd-route-start" /> Start · <span className="dd-route-line" /> route recorded on the app ({proof.route.points} point{proof.route.points === 1 ? "" : "s"}) · pin = finish</div>
                            </div>
                        )}
                        {loading && !proof ? <Skeleton active paragraph={{ rows: 4 }} /> : !proof?.events?.length ? (
                            <div className="cdb-empty">Nothing logged for this duty yet.</div>
                        ) : (
                            <ol className="dd-timeline">
                                {proof.events.map((e, i) => {
                                    const es = isMobile ? EVENT_STATE.onTime : (EVENT_STATE[e.state] || EVENT_STATE.pending);
                                    return (
                                        <li key={e._id || i} className="dd-event">
                                            <span className="dd-dot" style={{ background: es.color }} />
                                            <div className="dd-event-main">
                                                <div className="dd-event-top">
                                                    <span className="dd-event-type">{e.label && e.label !== e.type ? `${e.type} · ${e.label}` : e.type}</span>
                                                    {!isMobile && <span className="dd-event-state" style={{ color: es.color }}>{es.label}</span>}
                                                </div>
                                                <div className="dd-event-sub">
                                                    {e.dueAt && <>Due {dayjs(e.dueAt).format("HH:mm")}</>}
                                                    {e.at && <>{e.dueAt ? " · " : ""}{e.booked ? "Booked" : "Done"} {dayjs(e.at).format("HH:mm")}</>}
                                                    {e.automated && " · automatic, no phone check"}
                                                    {e.booked && " · not recorded on the app"}
                                                </div>
                                                {(e.selfie || e.photo || e.location) && (
                                                    <div className="dd-evidence">
                                                        {(e.selfie || e.photo) && (
                                                            <div className="dd-photos">
                                                                {e.selfie && <Image src={e.selfie} width={84} height={104} className="dd-thumb" alt="Officer selfie" />}
                                                                {e.photo && <Image src={e.photo} width={84} height={104} className="dd-thumb" alt="Photo" />}
                                                            </div>
                                                        )}
                                                        {e.location && <MiniMap location={e.location} map={e.map} geofence={e.geofence} />}
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                        {proof?.mapAttribution && <div className="dd-attribution">{proof.mapAttribution}</div>}
                    </section>

                    <section className="dd-section">
                        <h3>Reports</h3>
                        <div className="dd-actions">
                            {isMobile ? (
                                <button
                                    type="button"
                                    className="cdb-btn"
                                    disabled={!proof?.report || isBusy(proof?.report?.jobId)}
                                    onClick={() => downloadReport(proof.report.jobId, proof.report.templateId)}
                                >
                                    {isBusy(proof?.report?.jobId) ? busyIcon : proof?.report ? "Download duty report" : "No report submitted yet"}
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="cdb-btn"
                                        disabled={!isCompleted || isBusy(duty._id)}
                                        onClick={() => downloadDutyDetailPdf(duty._id)}
                                        title={isCompleted ? "" : "Available once the duty is completed"}
                                    >
                                        {isBusy(duty._id) ? busyIcon : isCompleted ? "Full duty report (PDF)" : "Full report once completed"}
                                    </button>
                                    <button
                                        type="button"
                                        className="cdb-btn"
                                        disabled={isBusy(duty._id)}
                                        onClick={() => downloadDetailDutyReport(duty._id, duty.siteId)}
                                    >
                                        Activity log (CSV)
                                    </button>
                                </>
                            )}
                        </div>
                        <button type="button" className="cdb-link dd-more" onClick={() => navigate(`/customer/reports/${duty.siteId}`)}>
                            All schedules & activity for this site ›
                        </button>
                    </section>
                </div>
            )}
        </Drawer>
    );
};
